import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTheme } from "../../../contexts/ThemeContext";
import { useLocation } from "react-router-dom";
import Modal from "../../shared/Modal";
import {
  XMarkIcon as XIcon,
  CalendarIcon,
  UserGroupIcon,
  MagnifyingGlassIcon as SearchIcon,
  LightBulbIcon,
  PlayIcon,
  PhoneIcon,
  VideoCameraIcon,
  ClockIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  BriefcaseIcon,
  PresentationChartLineIcon,
  CurrencyDollarIcon,
  ShieldExclamationIcon,
  ChartBarIcon,
  RocketLaunchIcon,
  ChatBubbleLeftRightIcon,
  DocumentMagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  BeakerIcon,
  HomeIcon,
  CheckIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { BriefingProvider } from "./briefing-room/BriefingContext";
import { useBriefingData } from "./briefing-room/hooks/useBriefingData";
import Tooltip from "../../shared/Tooltip";
import { useDemo } from "../../../contexts/DemoContext";
import { useTranscription } from "../../../contexts/TranscriptionContext";
import logger from '../../../utils/logger';

/* Hide scrollbars while maintaining functionality */
const scrollbarHideStyles = `
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const styleSheet = document.createElement("style");
styleSheet.textContent = scrollbarHideStyles;
document.head.appendChild(styleSheet);

// Badge component for consistent status indicators
const Badge = ({ children, color }) => {
  const { isDarkMode } = useTheme();
  const getBgColor = () => {
    const colors = {
      gray: isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-800',
      yellow: isDarkMode ? 'bg-yellow-500/20 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
      green: isDarkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-800',
    };
    return colors[color] || colors.gray;
  };

  return (
    <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${getBgColor()}`}>
      {children}
    </span>
  );
};

// Demo data constants
const demoMeetingDetails = {
  id: "demo-meeting-1",
  clientSince: "January 2022",
  date: new Date().toISOString(),
  medium: "phone",
  duration: "90 minutes"
};

const demoFormData = {
  members: [
    { userId: "demo-user-1", firstName: "Thomas", lastName: "Borer" },
    { userId: "demo-user-2", firstName: "Mary", lastName: "Borer" }
  ],
  incomes: [
    { id: "1", type: "Salary", amount: 120000, frequency: "Annual" },
    { id: "2", type: "Pension", amount: 40000, frequency: "Annual" }
  ],
  expenses: [
    { id: "1", type: "Housing", amount: 3000, frequency: "Monthly" },
    { id: "2", type: "Utilities", amount: 500, frequency: "Monthly" }
  ],
  debts: [
    { id: "1", type: "Mortgage", amount: 300000, rate: 3.5 }
  ],
  assets: [
    { id: "1", type: "401(k)", amount: 500000 },
    { id: "2", type: "IRA", amount: 250000 }
  ]
};

// Quadrant component for consistent styling
const Quadrant = ({ title, icon: Icon, children, className = "" }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`flex flex-col ${
      isDarkMode 
        ? 'bg-surface-default dark:bg-surface-default/10' 
        : 'bg-gray-50/80 shadow-sm border border-gray-100'
    } backdrop-blur-sm rounded-lg p-1 ${className}`}>
      <div className="flex items-center gap-1 mb-1">
        <Icon className={`w-3.5 h-3.5 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`} />
        <h2 className={`text-xs font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{title}</h2>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-hide space-y-0.5">{children}</div>
    </div>
  );
};

// Info item component for consistent styling
const InfoItem = ({
  icon: Icon,
  label,
  value,
  tooltip,
  onClick,
  className = "",
}) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`flex flex-col gap-0.5 ${className}`}>
      <div className={`text-[11px] ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        <span>{label}</span>
      </div>
      <div className={`text-[11px] ${isDarkMode ? 'text-white' : 'text-gray-900'} pl-4`}>
        {typeof value === 'string' ? (
          <span>{value}</span>
        ) : (
          value
        )}
        {tooltip && (
          <Tooltip content={tooltip}>
            <InformationCircleIcon 
              className={`w-3 h-3 ml-1 inline-block ${isDarkMode ? 'text-blue-300' : 'text-blue-600'} cursor-help`}
              aria-hidden="true"
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

// Research question component
const ResearchQuestion = ({
  question,
  status,
  answer,
  sources = [],
  reportLink,
  taskCreated,
  onClick
}) => {
  const { isDarkMode } = useTheme();
  const summaryPoints = answer?.split('. ').filter(Boolean);

  return (
    <li 
      onClick={onClick}
      className={`${
        isDarkMode 
          ? 'bg-surface-default/80 dark:bg-surface-default/5 hover:bg-surface-default/90' 
          : 'bg-white hover:bg-gray-50'
      } border border-gray-200 dark:border-gray-800 rounded-lg p-2 flex flex-col space-y-1.5 
      shadow-sm hover:shadow transition-all duration-200 cursor-pointer group`}
    >
      {/* Question + Two Tags */}
      <div className="flex justify-between items-start gap-1">
        <div className="flex items-center gap-1">
          <DocumentMagnifyingGlassIcon 
            className={`w-3 h-3 ${
              isDarkMode ? 'text-blue-400' : 'text-blue-600'
            } group-hover:scale-110 transition-transform duration-200`} 
          />
          <span className={`text-[11px] font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {question}
          </span>
        </div>
        <div className="flex gap-0.5 flex-shrink-0">
          <Badge color={
            status === 'complete' ? 'green' :
            status === 'in_progress' ? 'yellow' : 'gray'
          }>
            {status === 'complete' ? 'R&D Complete' :
             status === 'in_progress' ? 'R&D In Progress' :
             'Unanswered'}
          </Badge>
          {taskCreated && (
            <Badge color="gray">Task Created</Badge>
          )}
        </div>
      </div>

      {/* Bullet Summary */}
      {status === 'complete' && summaryPoints?.length > 0 && (
        <ul className={`list-disc list-inside text-xs space-y-1 ${
          isDarkMode ? 'text-gray-300' : 'text-gray-600'
        }`}>
          {summaryPoints.map((point, i) => <li key={i}>{point}</li>)}
        </ul>
      )}

      {/* Sources + Report Link */}
      {status === 'complete' && (
        <div className="flex items-center justify-between">
          {sources.length > 0 && (
            <div className="text-[10px] flex flex-wrap gap-1">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Sources: </span>
              {sources.map((source, i) => (
                <React.Fragment key={i}>
                  <a
                    href="#"
                    className={`${
                      isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                    } underline`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      /* TODO: Implement source link handling */
                    }}
                  >
                    {source}
                  </a>
                  {i < sources.length - 1 && <span className="mx-1">•</span>}
                </React.Fragment>
              ))}
            </div>
          )}
          {reportLink && (
            <a
              href={reportLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className={`text-[10px] underline ${
                isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
              }`}
            >
              View Full Report
            </a>
          )}
        </div>
      )}
    </li>
  );
};

// Discussion point component
const DiscussionPoint = ({ icon: Icon, title, taskCreated, tooltip, onClick }) => {
  const { isDarkMode } = useTheme();
  const [isChecked, setIsChecked] = useState(true); // Default to checked

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    setIsChecked(!isChecked);
  };

  return (
    <li 
      onClick={onClick}
      className={`${
        isDarkMode 
          ? 'bg-surface-default/80 dark:bg-surface-default/5 hover:bg-surface-default/90' 
          : 'bg-white hover:bg-gray-50'
      } border border-gray-200 dark:border-gray-800 rounded-lg p-1.5 
      shadow-sm hover:shadow transition-all duration-200 cursor-pointer group`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 flex-1">
          <Icon className={`w-3 h-3 ${
            isDarkMode ? 'text-blue-400' : 'text-blue-600'
          } group-hover:scale-110 transition-transform duration-200`} />
          <span className={`text-[11px] ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {title}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {tooltip && (
            <Tooltip content={tooltip}>
              <InformationCircleIcon 
                className={`w-2.5 h-2.5 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'} cursor-help`}
                aria-hidden="true"
              />
            </Tooltip>
          )}
          <button
            onClick={handleCheckboxClick}
            className={`w-3 h-3 rounded-full border transition-all duration-200 flex items-center justify-center ${
              isChecked
                ? `${isDarkMode ? 'bg-emerald-500/90 border-emerald-500' : 'bg-emerald-500 border-emerald-500'} ring-1 ring-emerald-500/20`
                : 'border-gray-400 hover:border-emerald-400'
            }`}
          >
            {isChecked && <CheckIcon className="w-2 h-2 text-white" />}
          </button>
        </div>
      </div>
    </li>
  );
};

// AI Copilot button component
const CopilotButton = ({ icon: Icon, label, onClick }) => {
  const { isDarkMode } = useTheme();
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
        isDarkMode 
          ? 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-300'
          : 'bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-100'
      } transition-colors w-full mb-2`}
    >
      <Icon className="w-4 h-4" />
      <span className="text-sm">{label}</span>
    </button>
  );
};

// Research tag component
const ResearchTag = ({ status }) => {
  const { isDarkMode } = useTheme();
  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1
      ${status === "complete" 
        ? `${isDarkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-50 text-green-700 border border-green-100'}`
        : `${isDarkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-50 text-blue-700 border border-blue-100'}`
      }`}
    >
      {status === "complete" ? (
        <>
          <CheckCircleIcon className="w-3 h-3" />
          R&D Complete
        </>
      ) : (
        <>
          <ArrowPathIcon className="w-3 h-3 animate-spin" />
          R&D In Progress
        </>
      )}
    </span>
  );
};

// Inner component that uses the briefing context
const BriefingRoomContent = ({
  meetingDetails,
  formData,
  pipelineData,
  getHouseholdName,
  onStartCall,
  onClose,
  isDemoMode
}) => {
  const { isDarkMode } = useTheme();
  const { onAction, demoStep } = useDemo();
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    console.log('🏠 BriefingRoomContent state:', { isDemoMode });
  }, [isDemoMode]);

  // Add spotlight effect for the start meeting button
  useEffect(() => {
    if (isDemoMode) {
      console.log('🎯 Setting up spotlight for start meeting button');
      const button = document.getElementById("demo-start-meeting-btn");
      if (button) {
        console.log('🎯 Found start meeting button, dispatching spotlight event');
        window.dispatchEvent(new CustomEvent("spotlight", {
          detail: {
            target: button,
            showCloseButton: false,
            disableAutoAdvance: true,
            overlayStyles: {
              pointerEvents: "auto",
              cursor: "pointer",
              background: "rgba(0, 0, 0, 0.25)",
              boxShadow: "0 0 0 2px #19fa6c, 0 0 20px #19fa6c40",
              borderRadius: "8px"
            },
            spotlightStyles: {
              pointerEvents: "none",
              zIndex: 9999999
            },
            labelStyles: {
              background: "linear-gradient(130deg, #212c23 60%, #17453c 100%)",
              color: "#e3ffe9",
              padding: "24px 30px",
              borderRadius: "14px",
              boxShadow: "0 4px 32px 0 #19fa6c38, 0 2px 12px rgba(0,0,0,0.25)",
              fontSize: "1.07rem",
              lineHeight: "1.5",
              maxWidth: "410px",
              minWidth: "320px",
              position: "relative",
              border: "none",
              zIndex: 9999999
            }
          }
        }));
      }
    }
  }, [isDemoMode]);

  // Add demo data when in demo mode
  const demoFormData = isDemoMode ? {
    members: [
      { userId: "demo-user-1", firstName: "Thomas", lastName: "Borer" },
      { userId: "demo-user-2", firstName: "Mary", lastName: "Borer" }
    ],
    incomes: [
      { id: "1", type: "Salary", amount: 120000, frequency: "Annual" },
      { id: "2", type: "Pension", amount: 40000, frequency: "Annual" }
    ],
    expenses: [
      { id: "1", type: "Housing", amount: 3000, frequency: "Monthly" },
      { id: "2", type: "Utilities", amount: 500, frequency: "Monthly" }
    ],
    debts: [
      { id: "1", type: "Mortgage", amount: 300000, rate: 3.5 }
    ],
    assets: [
      { id: "1", type: "401(k)", amount: 500000 },
      { id: "2", type: "IRA", amount: 250000 }
    ]
  } : formData;

  const demoMeetingDetails = isDemoMode ? {
    id: "demo-meeting-1",
    clientSince: "January 2022",
    date: new Date().toISOString(),
    medium: "phone",
    duration: "90 minutes"
  } : meetingDetails;

  const effectiveFormData = isDemoMode ? demoFormData : formData;
  const effectiveMeetingDetails = isDemoMode ? demoMeetingDetails : meetingDetails;

  useEffect(() => {
    if (isDemoMode) {
      console.log('🏠 BriefingRoom state update:', { isDemoMode, demoStep, isOpen: true, hasData, meetingId: 'demo-meeting-1' });
      setHasData(true);
    }
  }, [isDemoMode, demoStep]);

  const handleStartMeeting = () => {
    console.log('🎯 Starting meeting from briefing room');
    onStartCall();
    onClose();
  };

  // Create a local function to format the household name
  const getFormattedHouseholdName = () => {
    if (isDemoMode) return "Borer Household";
    if (!effectiveFormData?.members?.length) return "Unknown Household";
    return getHouseholdName(effectiveFormData.members);
  };

  const householdName = getFormattedHouseholdName();
  logger.info("Generated Household Name:", householdName);

  // Early return for loading state
  if (!isDemoMode && (!effectiveMeetingDetails || !effectiveFormData)) {
    return (
      <div className="text-center py-6">
        <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Loading meeting details...</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-1 ${isDarkMode ? 'dark' : ''}`}>
      <div className="flex justify-between items-start">
        <div>
          <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            Meeting Briefing Room
          </h3>
        </div>
        <button
          onClick={onClose}
          className={`rounded-full p-0.5 ${
            isDarkMode 
              ? 'hover:bg-surface-default dark:bg-surface-default/10' 
              : 'hover:bg-gray-100'
          } transition-colors`}
        >
          <XIcon className={`w-3.5 h-3.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-1">
        {/* Meeting Overview Quadrant */}
        <Quadrant title="Meeting Overview" icon={CalendarIcon} className="h-fit">
          <div className="flex gap-2">
            <div className="space-y-1 flex-1">
              <InfoItem
                icon={CalendarIcon}
                label="Title"
                value={`Annual Review – ${householdName}`}
              />
              <InfoItem
                icon={CalendarIcon}
                label="Date"
                value={(() => {
                  const today = new Date();
                  return today.toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  });
                })()}
              />
              <InfoItem
                icon={ClockIcon}
                label="Time"
                value="8:00 AM–9:30 AM (Duration: 90 minutes)"
              />
            </div>
            <div className="space-y-1 flex-1">
              <InfoItem
                icon={PhoneIcon}
                label="Medium"
                value={
                  <div className="flex items-center gap-1">
                    <span>Phone Call</span>
                    <button
                      onClick={() => {/* TODO: Implement toggle functionality */}}
                      className="px-1 py-0.5 text-[10px] bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
                    >
                      Switch to Video
                    </button>
                  </div>
                }
              />
              <InfoItem
                icon={PhoneIcon}
                label="Best Contact"
                value="(979) 931-6078"
              />
              <div className="mt-0 pb-3">
                <h4 className={`text-[11px] ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Attendees:</h4>
                <div className="space-y-1">
                  <div className={`flex items-center gap-1 text-[11px] ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <UserGroupIcon className="w-3 h-3" />
                    <span>Thomas & Mary Borer, Advisor (You)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <button
              id="demo-start-meeting-btn"
              onClick={handleStartMeeting}
              className="w-full inline-flex items-center justify-center px-2 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-0 transition-all"
            >
              <PlayIcon className="w-3 h-3 mr-1" />
              Start Meeting
            </button>
          </div>
        </Quadrant>

        {/* Client Summary Quadrant */}
        <Quadrant title={`${householdName} Summary`} icon={HomeIcon} className="h-fit">
          <div className="grid grid-cols-2 gap-2">
            {/* Left Column */}
            <div className="space-y-2">
              <div className="pl-0">
                <InfoItem
                  icon={CalendarIcon}
                  label="Clients Since"
                  value="January 2022"
                />
              </div>
              <div className={`${isDarkMode ? 'bg-surface-default dark:bg-surface-default/5' : 'bg-gray-50'} rounded-lg p-1.5 pl-0`}>
                <h4 className={`text-[11px] font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-1 pl-1.5`}>
                  Lifecycle Stage
                </h4>
                <div className="space-y-1 pl-1.5">
                  <div className="flex items-center gap-1">
                    <p className={`text-[11px] ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Thomas: Employed (Full-Time)</p>
                    <Badge color="green">Active</Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <p className={`text-[11px] ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Mary: Retired (2024)</p>
                    <Badge color="green">Active</Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-1">
              <div className="pl-0">
                <InfoItem
                  icon={CalendarIcon}
                  label="Last Meeting"
                  value="September 2024"
                  className="hover:cursor-help"
                  tooltip="See Details"
                />
              </div>
              <div className={`${isDarkMode ? 'bg-surface-default dark:bg-surface-default/5' : 'bg-gray-50'} rounded-lg p-1.5 pl-0`}>
                <h4 className={`text-[11px] font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-1 pl-1.5`}>
                  Recent Changes
                </h4>
                <ul className="space-y-1 pl-1.5">
                  <li className={`flex items-center gap-1 text-[11px] ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    <BriefcaseIcon className="w-3 h-3 flex-shrink-0" />
                    Thomas: Promotion + RSUs
                  </li>
                  <li className={`flex items-center gap-1 text-[11px] ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    <CalendarIcon className="w-3 h-3 flex-shrink-0" />
                    Switzerland Trip Fall 2024
                  </li>
                  <li className={`flex items-center gap-1 text-[11px] ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    <CurrencyDollarIcon className="w-3 h-3 flex-shrink-0" />
                    Cash flow boost from bonus
                  </li>
                  <li className={`flex items-center gap-1 text-[11px] ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    <ShieldExclamationIcon className="w-3 h-3 flex-shrink-0" />
                    Mary: Started Medicare Dec '24
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </Quadrant>

        {/* Pre-Review Questions & R&D */}
        <Quadrant
          title="Pre-Review Questions & Research"
          icon={DocumentMagnifyingGlassIcon}
          className="h-full"
        >
                    <div className="flex flex-col h-full">
            <ul className="flex-1 space-y-0.5 px-2 py-1">
              <ResearchQuestion
                question="What's the impact of RSUs on my tax plan and when do I owe taxes?"
                status="complete"
                answer="RSUs are taxed as ordinary income at vesting. If held post-vesting, capital gains apply to appreciation. Recommend tax-withholding strategy to avoid April surprises"
                sources={["IRS Publication 525", "Fidelity RSU Guide"]}
                reportLink="https://yourapp.com/reports/rsu-integration"
                taskCreated={true}
                onClick={() => {/* TODO: Implement view/edit functionality */}}
              />

              <ResearchQuestion
                question="How would early retirement at 64 affect our long-term plan?"
                status="in_progress"
                taskCreated={true}
                onClick={() => {/* TODO: Implement view/edit functionality */}}
              />
            </ul>

            <div className="px-2 pt-0 pb-1.5">
              <button
                onClick={() => {/* TODO: Implement research functionality */}}
                className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-md ${
                  isDarkMode 
                    ? 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border border-blue-500/30'
                    : 'bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200'
                } transition-all duration-200 hover:shadow-sm`}
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Add Research
              </button>
            </div>
          </div>
        </Quadrant>

        {/* Suggested Discussion Points */}
        <Quadrant
          title="Suggested Discussion Points"
          icon={ChatBubbleLeftRightIcon}
          className="h-full"
        >
          <div className="flex flex-col h-full">
            <ul className="flex-1 space-y-0.5 px-2 py-1">
              <DiscussionPoint
                icon={BriefcaseIcon}
                title="Stock Options: Confirm RSU details and review R&D report"
                taskCreated={true}
                onClick={() => {/* TODO: Implement view/edit functionality */}}
              />
              <DiscussionPoint
                icon={PresentationChartLineIcon}
                title="Risk Tolerance: Discuss updated profile or send reassessment link"
                taskCreated={false}
                onClick={() => {/* Create planning task */}}
              />
              <DiscussionPoint
                icon={CurrencyDollarIcon}
                title="Year-End Tax Plan: Review potential Roth conversion up to $40k"
                taskCreated={false}
                onClick={() => {/* Create planning task */}}
              />
              <DiscussionPoint
                icon={ShieldExclamationIcon}
                title="Estate Planning: Frame value + ease of integration early"
                taskCreated={false}
                onClick={() => {/* Create planning task */}}
              />
              <DiscussionPoint
                icon={ChartBarIcon}
                title="Alts Exposure: Offer PE opportunity (A+ due diligence)"
                taskCreated={false}
                onClick={() => {/* Create planning task */}}
              />
              <DiscussionPoint
                icon={RocketLaunchIcon}
                title="Retirement Flexibility: Alternative early retirement scenario"
                taskCreated={false}
                onClick={() => {/* Create planning task */}}
              />
            </ul>

            <div className="px-2 pt-0 pb-1.5">
              <button
                onClick={() => {/* TODO: Implement add discussion point functionality */}}
                className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-md ${
                  isDarkMode 
                    ? 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border border-blue-500/30'
                    : 'bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200'
                } transition-all duration-200 hover:shadow-sm`}
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Add Discussion Points
              </button>
            </div>
          </div>
        </Quadrant>
      </div>
    </div>
  );
};

BriefingRoomContent.propTypes = {
  meetingDetails: PropTypes.shape({
    id: PropTypes.string.isRequired,
    clientSince: PropTypes.string,
    date: PropTypes.string.isRequired,
    medium: PropTypes.string.isRequired,
    duration: PropTypes.string,
  }),
  formData: PropTypes.shape({
    members: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.string.isRequired,
      }),
    ),
  }),
  pipelineData: PropTypes.object,
  getHouseholdName: PropTypes.func.isRequired,
  onStartCall: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isDemoMode: PropTypes.bool
};

// Main component wrapper with BriefingProvider
const MeetingBriefingRoom = ({
  isOpen = false,
  onClose = () => {},
  meetingDetails = null,
  formData = null,
  pipelineData = null,
  getHouseholdName = (members) => {
    if (!members?.length) return "Unknown Household";
    const [primary] = members;
    return `${primary.lastName} Household`;
  },
  onStartCall = () => {},
  isDemoMode: propsDemoMode
}) => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const { onAction, isDemoMode: contextDemoMode, demoStep } = useDemo();

  // Initialize demo when navigated here with startDemo flag
  useEffect(() => {
    if (location.state?.startDemo && !contextDemoMode) {
      console.log('🎯 MeetingBriefingRoom initializing demo');
      onAction({ type: 'START_DEMO' });
    }
  }, [location.state, contextDemoMode, onAction]);

  // Combine demo mode from props and context
  const effectiveDemoMode = propsDemoMode || contextDemoMode;

  // Don't render if not open or if in demo mode and past step 1
  if (!isOpen || (effectiveDemoMode && demoStep > 1)) {
    return null;
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      width="clamp(400px, 80vw, 1080px)"
      maxHeight="tall"
      className={`${isDarkMode ? 'bg-gray-800' : 'bg-surface-default dark:bg-surface-default'}`}
    >
      <BriefingRoomContent
        meetingDetails={meetingDetails}
        formData={formData}
        pipelineData={pipelineData}
        getHouseholdName={getHouseholdName}
        onStartCall={onStartCall}
        onClose={onClose}
        isDemoMode={effectiveDemoMode}
      />
    </Modal>
  );
};

MeetingBriefingRoom.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  meetingDetails: PropTypes.object,
  formData: PropTypes.object,
  pipelineData: PropTypes.object,
  getHouseholdName: PropTypes.func,
  onStartCall: PropTypes.func,
  isDemoMode: PropTypes.bool
};

export default MeetingBriefingRoom;
